import type { ProjectDetail } from '@fos/shared/contracts/projects';
import { BookOpen, History, ListTodo, type LucideIcon, Timer, Wrench } from 'lucide-react';
import type { ComponentType } from 'react';
import { EmptyState } from '../../ui/States';

/**
 * Miejsca na treści innych modułów w widokach Projektów (wypełnia kompozycja Fali 1/2, bez zmian w tym module):
 *  - zakładka „Usługi” na liście projektów (Figma 07: usługi klientów obok projektów) — moduł usług,
 *  - zakładki szczegółów projektu: Zadania (work), Czas (time, Fala 2), Wiedza (knowledge, Fala 2), Historia (audyt).
 *
 * Przykład (np. w module zadań): `setProjectTab('tasks', ({ project }) => <TaskList projectId={project.id} />)`.
 */

export type ProjectTabKey = 'tasks' | 'time' | 'knowledge' | 'history';
export type ProjectTabComponent = ComponentType<{ project: ProjectDetail }>;

const PLACEHOLDERS: Record<ProjectTabKey, { icon: LucideIcon; title: string; description: string }> = {
  tasks: {
    icon: ListTodo,
    title: 'Zadania projektu',
    description: 'Tu pojawią się otwarte i zakończone zadania tego projektu.',
  },
  time: {
    icon: Timer,
    title: 'Czas projektu',
    description: 'Tu zobaczysz wpisy czasu i wykorzystanie budżetu godzin.',
  },
  knowledge: {
    icon: BookOpen,
    title: 'Wiedza projektu',
    description: 'Tu pojawią się notatki, decyzje i pliki projektu.',
  },
  history: {
    icon: History,
    title: 'Historia zmian',
    description: 'Tu zobaczysz, kto i kiedy zmienił projekt.',
  },
};

const tabs: Partial<Record<ProjectTabKey, ProjectTabComponent>> = {};

export function setProjectTab(key: ProjectTabKey, component: ProjectTabComponent | null): void {
  if (component) tabs[key] = component;
  else delete tabs[key];
}

export function ProjectTabContent({ tab, project }: { tab: ProjectTabKey; project: ProjectDetail }) {
  const Registered = tabs[tab];
  if (Registered) return <Registered project={project} />;
  const p = PLACEHOLDERS[tab];
  return (
    <div className="rounded-xl border border-dashed border-line">
      <EmptyState icon={p.icon} title={p.title} description={p.description} />
    </div>
  );
}

let servicesTab: ComponentType | null = null;

/** Zakładka „Usługi” na liście projektów (lista usług klientów z modułu usług). */
export function setProjectsServicesTab(component: ComponentType | null): void {
  servicesTab = component;
}

export function ProjectsServicesTab() {
  const Registered = servicesTab;
  if (Registered) return <Registered />;
  return (
    <div className="mt-5 rounded-xl border border-dashed border-line">
      <EmptyState
        icon={Wrench}
        title="Usługi klientów"
        description="Tu pojawią się umowy utrzymania, konsultacje i pakiety godzin klientów."
      />
    </div>
  );
}

// ── Sekcje Przeglądu projektu ───────────────────────────────────────────────
export interface ProjectOverviewSection {
  key: string;
  /** `left` — pod celem, zakresem i linkami; `right` — nad zespołem (np. otwarte zadania) */
  column: 'left' | 'right';
  /** kolejność w kolumnie (rosnąco) */
  order: number;
  component: ProjectTabComponent;
}

const overviewSections = new Map<string, ProjectOverviewSection>();

/** Dodaje albo podmienia sekcję Przeglądu projektu (po kluczu), np. otwarte zadania z modułu zadań. */
export function registerProjectOverviewSection(section: ProjectOverviewSection): void {
  overviewSections.set(section.key, section);
}

export function getProjectOverviewSections(column: ProjectOverviewSection['column']): ProjectOverviewSection[] {
  return [...overviewSections.values()].filter((s) => s.column === column).sort((a, b) => a.order - b.order);
}
